import { z } from "zod";
import { REVIEW_ORIGIN_TEXT_MAX, type ReviewOriginTextKey } from "../reviewOriginTexts";

/**
 * Plánovací blok — prázdný stav sekce zájezdů na stránce destinace. Destinace si vybere
 * redakční variantu 1–3 (text z Nastavení → Obsah webu, klíč `content.planningBlockN`)
 * nebo má vlastní text. Jediný domov modelu: web z něj skládá text plochy, admin náhled
 * ukazuje tentýž text a Nastavení z něj bere klíče.
 */

/** Nejdelší text varianty plánovacího bloku i vlastního textu destinace. */
export const PLANNING_BLOCK_TEXT_MAX = 1000;

/** Redakční varianty plánovacího bloku (klíče `content.planningBlock1–3`). */
export const PLANNING_BLOCK_VARIANTS = [1, 2, 3] as const;
export type PlanningBlockVariant = (typeof PLANNING_BLOCK_VARIANTS)[number];

export function isPlanningBlockVariant(value: unknown): value is PlanningBlockVariant {
  return (PLANNING_BLOCK_VARIANTS as readonly unknown[]).includes(value);
}

export type PlanningBlockKey = `planningBlock${PlanningBlockVariant}`;

/** Klíč textu varianty v kategorii nastavení `content`. */
export function planningBlockKey(variant: PlanningBlockVariant): PlanningBlockKey {
  return `planningBlock${variant}`;
}

const variantText = z.string().max(PLANNING_BLOCK_TEXT_MAX);
const reviewOriginText = z.string().max(REVIEW_ORIGIN_TEXT_MAX);

/**
 * Nastavení → Obsah webu (kategorie `content`): tři texty variant plánovacího bloku
 * a čtyři texty o původu recenzí (vize F2.8, `packages/shared/src/reviewOriginTexts.ts`).
 * Prostý text, prázdný = web použije výchozí znění (to žije v kódu, do Nastavení se
 * nezapisuje). Uloží se jen tyhle klíče — kategorie je veřejná (`GET /api/settings/public`).
 */
export const contentSettingsSchema = z
  .object({
    planningBlock1: variantText,
    planningBlock2: variantText,
    planningBlock3: variantText,
    reviewsIntro: reviewOriginText,
    reviewsOriginClients: reviewOriginText,
    reviewsOriginGoogle: reviewOriginText,
    reviewsOriginElsewhere: reviewOriginText,
  } satisfies Record<PlanningBlockKey | ReviewOriginTextKey, z.ZodString>)
  .partial();

/** Texty kategorie `content` — tvar uloženého nastavení i veřejné projekce. */
export type ContentSettings = z.infer<typeof contentSettingsSchema>;

/** Pole destinace, ze kterých se skládá plánovací blok. */
export interface PlanningBlockDestination {
  planningBlockVariant?: number | null;
  planningBlockText?: string | null;
}

/**
 * Hodnota nastavení jako text. Úložiště nastavení hodnoty parsuje jako JSON, takže text
 * složený jen z číslic dorazí jako číslo (`JSON.parse("123")`) — pořád je to text.
 */
export const settingText = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";

/**
 * Text plánovacího bloku destinace: neprázdný vlastní text destinace > neprázdný text
 * vybrané varianty z Nastavení → Obsah webu > `null` (= výchozí věta plochy). Varianta
 * se z rodiče na lokalitu NEDĚDÍ — čte se jen z předané destinace.
 */
export function resolvePlanningBlockText(
  destination: PlanningBlockDestination,
  content: ContentSettings | null | undefined,
): string | null {
  const own = settingText(destination.planningBlockText);
  if (own) return own;
  const variant = destination.planningBlockVariant;
  if (!isPlanningBlockVariant(variant)) return null;
  return settingText(content?.[planningBlockKey(variant)]) || null;
}
